import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarClock, CheckCircle2, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/landing/Reveal";
import { PRIORITY, ResolveControl } from "@/components/ActionPlan";
import { api } from "@/lib/api";

const GROUPS = [
  ["Crítica", "Atender en 7 días o menos"],
  ["Alta", "Atender en 30 días o menos"],
  ["Media", "Atender en 60 días o menos"],
];

const Item = ({ it, onChange }) => {
  const p = it.resolved ? { bar: "#10B981" } : PRIORITY[it.priority] ?? PRIORITY.Media;
  return (
    <motion.li layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="relative grid grid-cols-12 gap-4 py-6 pl-5 border-b border-white/10" data-testid={`center-item-${it.audit_id}-${it.question_id}`}>
      <span className="absolute left-0 top-6 bottom-6 w-[3px]" style={{ background: p.bar }} />
      <div className="col-span-12 md:col-span-8 min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.14em]">
          <span className="text-[#F59E0B]">{it.module_name}</span>
          <span className="text-[#F5F1E8]/55">{it.company} · {it.site}</span>
        </div>
        <p className={`mt-2 text-base sm:text-lg font-semibold leading-snug ${it.resolved ? "line-through decoration-emerald-400/60 text-[#F5F1E8]/55" : ""}`}>{it.action}</p>
        <p className="mt-1.5 text-sm text-[#F5F1E8]/60">{it.topic ? `${it.topic} — ` : ""}{it.question}</p>
        <Link to={`/resumen/${it.audit_id}`} data-testid={`center-audit-link-${it.audit_id}-${it.question_id}`} className="group mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F5F1E8]/70 hover:text-[#F59E0B] transition-colors">
          Ver auditoría del {new Date(it.audit_date).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })} <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:rotate-45" />
        </Link>
      </div>
      <div className="col-span-12 md:col-span-4 flex md:flex-col md:items-end flex-wrap items-center gap-3">
        {it.resolved ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold bg-emerald-500/10 border-emerald-500/40 text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Resuelta</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#F5F1E8]/70"><CalendarClock className="w-3.5 h-3.5" /> máx. {it.days} días</span>
        )}
        <ResolveControl auditId={it.audit_id} it={it} onAudit={onChange} />
      </div>
    </motion.li>
  );
};

export default function ActionCenter() {
  const [items, setItems] = useState(null);
  const [mod, setMod] = useState("todos");

  const load = useCallback(() => {
    api.get("/action-plan").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const modules = useMemo(() => [...new Set((items ?? []).map((i) => i.module_name))], [items]);
  const list = (items ?? []).filter((i) => mod === "todos" || i.module_name === mod);
  const pending = list.filter((i) => !i.resolved);
  const done = list.filter((i) => i.resolved);
  const pct = list.length ? Math.round((done.length / list.length) * 100) : 0;

  return (
    <main className="min-h-screen" data-testid="action-center-page">
      <Navbar variant="app" />
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 pt-32 pb-24">
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Fix tracking centralizado</div>
        <h1 className="font-display-x mt-5 text-5xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tight"><MaskLines lines={["Plan de", "acción"]} /></h1>
        <p className="mt-6 max-w-2xl text-[#F5F1E8]/70">Todos los “No” de tus auditorías STPS e ISO, ordenados por prioridad. Sube la foto de cierre para marcar cada acción como resuelta y recalcular el score de su auditoría.</p>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="center-stats">
          {[["Pendientes", pending.length, "text-[#F5F1E8]"], ["Críticas", pending.filter((i) => i.priority === "Crítica").length, "text-rose-400"], ["Resueltas", done.length, "text-emerald-400"], ["Avance", `${pct}%`, "text-emerald-400"]].map(([k, v, c]) => (
            <div key={k} className="bg-[#0B0F17] p-5" data-testid={`center-stat-${k.toLowerCase()}`}><div className={`font-display-x text-4xl font-black ${c}`}>{v}</div><div className="font-mono text-xs text-[#F5F1E8]/60 mt-1">{k}</div></div>
          ))}
        </div>

        {modules.length > 1 && (
          <div className="mt-8 flex gap-2 overflow-x-auto pb-2" data-testid="center-module-filters">
            {["todos", ...modules].map((m) => (
              <button key={m} onClick={() => setMod(m)} data-testid={`center-filter-${m.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                className={`shrink-0 px-4 py-2 rounded-full border text-sm transition-colors ${mod === m ? "bg-[#F5F1E8] text-[#0B0F17] border-[#F5F1E8]" : "border-white/15 text-[#F5F1E8]/75 hover:border-white/40"}`}>
                {m === "todos" ? "Todas las normas" : m}
              </button>
            ))}
          </div>
        )}

        {items === null && <div className="py-16 font-mono text-sm text-[#F5F1E8]/60">Cargando…</div>}
        {items?.length === 0 && (
          <div className="mt-12 border border-emerald-500/30 bg-emerald-500/5 p-8 flex items-center gap-4 text-emerald-300" data-testid="center-empty"><ShieldCheck className="w-6 h-6" /> No tienes hallazgos registrados. Realiza una auditoría desde tu panel.</div>
        )}

        {GROUPS.map(([prio, hint]) => {
          const g = pending.filter((i) => i.priority === prio);
          if (!g.length) return null;
          const c = PRIORITY[prio];
          return (
            <section key={prio} className="mt-14" data-testid={`center-group-${prio === "Crítica" ? "critica" : prio.toLowerCase()}`}>
              <div className="flex items-baseline justify-between gap-4 border-b border-white/10 pb-4">
                <h2 className={`font-display text-2xl sm:text-3xl font-bold ${c.text}`}>{prio} <span className="text-[#F5F1E8]/40 font-mono text-base">{g.length}</span></h2>
                <span className="font-mono text-xs text-[#F5F1E8]/60">{hint}</span>
              </div>
              <ol>{g.map((it) => <Item key={`${it.audit_id}-${it.question_id}`} it={it} onChange={load} />)}</ol>
            </section>
          );
        })}

        {done.length > 0 && (
          <section className="mt-14" data-testid="center-group-resueltas">
            <div className="flex items-baseline justify-between gap-4 border-b border-white/10 pb-4">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-emerald-400">Resueltas <span className="text-[#F5F1E8]/40 font-mono text-base">{done.length}</span></h2>
            </div>
            <ol>{done.map((it) => <Item key={`${it.audit_id}-${it.question_id}`} it={it} onChange={load} />)}</ol>
          </section>
        )}
      </div>
    </main>
  );
}
